// Private photos, documents and portfolios for the website. Mirrors mobile/src/services/attachments.ts.
// Access is decided by the database (see supabase/migrations/202609260020_attachments.sql).

export const attachmentBucket = 'resident-service-360-files';
export const imageTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
export const documentTypes = ['application/pdf'];
export const maxImageBytes = 10 * 1024 * 1024;
export const maxDocumentBytes = 20 * 1024 * 1024;
export const maxImageEdge = 2048;
export const imageQuality = 0.8;
export const signedUrlSeconds = 120;

export const isImage = (type) => imageTypes.includes(String(type || '').toLowerCase());
export const isDocument = (type) => documentTypes.includes(String(type || '').toLowerCase());
export const maxFilesFor = (scope) => (scope === 'work_record' || scope === 'portfolio' ? 30 : 10);

const extensionTypes = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', heif: 'image/heif', webp: 'image/webp', pdf: 'application/pdf' };
export function typeOf(file) {
  const reported = String(file?.type || '').toLowerCase();
  if (reported && reported !== 'application/octet-stream') return reported === 'image/jpg' ? 'image/jpeg' : reported;
  const ext = String(file?.name || '').split('.').pop().toLowerCase();
  return extensionTypes[ext] || reported;
}

/** Plain-English problem with a file, or null when it can be uploaded. */
export function validateFile(file, kinds = 'photos-and-documents') {
  const type = typeOf(file);
  const image = isImage(type), doc = isDocument(type);
  if (!image && !doc) return 'Only photos (JPEG, PNG, HEIC or WebP) and PDF documents can be added.';
  if (image && kinds === 'documents') return 'Only PDF documents can be added here.';
  if (doc && kinds === 'photos') return 'Only photos can be added here.';
  const limit = doc ? maxDocumentBytes : maxImageBytes;
  if (file.size > limit) return `${doc ? 'Documents' : 'Photos'} must be ${Math.round(limit / 1048576)} MB or smaller.`;
  if (file.size === 0) return 'That file is empty.';
  return null;
}

/** Target size that keeps the longest side at or under 2048 pixels. */
export function fitWithin(width, height, edge = maxImageEdge) {
  const longest = Math.max(width, height);
  if (longest <= edge) return { width, height };
  const scale = edge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Draws the photo onto a fresh canvas and saves a new JPEG. Only pixels are copied, so
 * location, camera and time details (EXIF) are left behind. Browsers that cannot read
 * a format (often HEIC outside Safari) refuse it rather than uploading the original.
 */
export async function reencodeImage(file, deps = {}) {
  const decode = deps.decode || ((blob) => createImageBitmap(blob, { imageOrientation: 'from-image' }));
  const makeCanvas = deps.makeCanvas || ((w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h }));
  let bitmap;
  try { bitmap = await decode(file); }
  catch { throw new Error('This browser cannot read that photo. Save it as a JPEG and try again.'); }
  const size = fitWithin(bitmap.width, bitmap.height);
  const canvas = makeCanvas(size.width, size.height);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close?.();
  const blob = await new Promise((resolve, reject) => canvas.toBlob((out) => (out ? resolve(out) : reject(new Error('Could not prepare that photo.'))), 'image/jpeg', imageQuality));
  return { blob, type: 'image/jpeg', width: size.width, height: size.height, name: 'photo.jpg' };
}

const friendly = {
  ATTACHMENT_FORBIDDEN: 'You cannot add files here.',
  FILE_TYPE_NOT_ALLOWED: 'Only photos and PDF documents can be added.',
  FILE_TOO_LARGE: 'That file is too large.',
  TOO_MANY_FILES: 'This already has the most files allowed. Remove one to add another.',
  UPLOAD_NOT_FOUND: 'The upload did not finish. Please try again.',
  ATTACHMENT_NOT_FOUND: 'That file is no longer available.',
  SIGN_IN_REQUIRED: 'Sign in to add files.',
};
function check(error) {
  if (!error) return;
  const code = Object.keys(friendly).find((key) => String(error.message || '').includes(key));
  throw new Error(code ? friendly[code] : error.message);
}

/** Prepares (photos are re-encoded), reserves, uploads and confirms one file. */
export async function uploadFile(client, { scope, scopeId, file, caption = '' }, deps = {}) {
  const problem = validateFile(file);
  if (problem) throw new Error(problem);
  const prepared = isImage(typeOf(file)) ? await (deps.reencode || reencodeImage)(file) : { blob: file, type: 'application/pdf', width: null, height: null };
  const size = prepared.blob.size;
  const again = validateFile({ name: prepared.name || file.name, type: prepared.type, size });
  if (again) throw new Error(again);
  const { data: created, error: createError } = await client.rpc('create_attachment_upload', {
    p_scope: scope, p_scope_id: scopeId, p_mime_type: prepared.type, p_size_bytes: size,
    p_width: prepared.width, p_height: prepared.height, p_caption: String(caption).trim(),
  });
  check(createError);
  const row = Array.isArray(created) ? created[0] : created;
  if (!row) throw new Error('Could not start the upload. Please try again.');
  const { error: uploadError } = await client.storage.from(attachmentBucket).upload(row.storage_path, prepared.blob, { contentType: prepared.type, upsert: false });
  if (uploadError) {
    try { await client.rpc('remove_attachment', { p_attachment_id: row.id }); } catch { /* the reserved record expires unused */ }
    throw new Error(`Upload failed: ${uploadError.message}`);
  }
  const { data: done, error: doneError } = await client.rpc('complete_attachment_upload', { p_attachment_id: row.id });
  check(doneError);
  if (done?.status === 'rejected') {
    await client.storage.from(attachmentBucket).remove([row.storage_path]);
    throw new Error('That file did not match its type and was not added.');
  }
  return done;
}

export async function listFiles(client, scope, scopeId) {
  const { data, error } = await client.rpc('list_attachments', { p_scope: scope, p_scope_id: scopeId });
  check(error);
  return data || [];
}

/** Short-lived download links (60 to 300 seconds) for ready files, keyed by storage path. */
export async function signedLinks(client, rows, seconds = signedUrlSeconds) {
  const paths = rows.filter((row) => row.status === 'ready').map((row) => row.storage_path);
  if (!paths.length) return {};
  const { data, error } = await client.storage.from(attachmentBucket).createSignedUrls(paths, Math.min(Math.max(seconds, 60), 300));
  check(error);
  const out = {};
  for (const item of data || []) if (item.path && item.signedUrl && !item.error) out[item.path] = item.signedUrl;
  return out;
}

export async function removeFile(client, id) {
  const { data, error } = await client.rpc('remove_attachment', { p_attachment_id: id });
  check(error);
  if (data) await client.storage.from(attachmentBucket).remove([data]);
}
